import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import { outreachApi } from '../api/outreachApi';

// A penalty total, so lower is better and 5.0 is the usual spam cut-off.
// Mail-tester shows the inverted scale, which is why "10/10" means the same.
const bandFor = (score) => (score >= 5 ? 'bad' : score >= 3 ? 'warn' : 'good');

export default function SpamCheckPanel({ campaignId, stepCount = 1, onClose }) {
  const { t } = useTranslation('common');
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [stepIndex, setStepIndex] = useState(0);
  const [leadSearch, setLeadSearch] = useState('');
  const [leadMatches, setLeadMatches] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);

  // Debounced: searches contacts enrolled in this campaign as the user types.
  useEffect(() => {
    if (!leadSearch.trim()) {
      setLeadMatches([]);
      return undefined;
    }
    const id = setTimeout(async () => {
      const page = await outreachApi.listLeads({ campaign_id: campaignId, search: leadSearch, limit: 8 });
      setLeadMatches(page.leads);
    }, 300);
    return () => clearTimeout(id);
  }, [leadSearch, campaignId]);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      setReport(await outreachApi.spamCheckCampaign(campaignId, { stepIndex, leadId: selectedLead?.id }));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="import-wizard">
      <div className="import-wizard-head">
        <h2 className="unibox-detail-subject">{t('outreach.check.title')}</h2>
        <button className="unibox-page-button" onClick={onClose}>
          {t('outreach.close')}
        </button>
      </div>

      <p className="unibox-reply-hint">{t('outreach.check.explainer')}</p>

      <div className="unibox-reply-actions">
        {stepCount > 1 && (
          <select className="unibox-select" value={stepIndex} onChange={(e) => setStepIndex(Number(e.target.value))}>
            {Array.from({ length: stepCount }, (_, i) => (
              <option key={i} value={i}>
                {t('outreach.campaign.step', { number: i + 1 })}
              </option>
            ))}
          </select>
        )}
        <div className="campaign-variable-field">
          <input
            className="unibox-select"
            placeholder={t('outreach.check.leadPlaceholder')}
            value={selectedLead ? selectedLead.email : leadSearch}
            onChange={(e) => {
              setSelectedLead(null);
              setLeadSearch(e.target.value);
            }}
          />
          {!selectedLead && leadMatches.length > 0 && (
            <div className="campaign-variable-suggest">
              {leadMatches.map((lead) => (
                <button
                  key={lead.id}
                  type="button"
                  onClick={() => {
                    setSelectedLead(lead);
                    setLeadMatches([]);
                  }}
                >
                  {lead.email} {lead.first_name && <span className="lead-history-source">{lead.first_name}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        <button className="unibox-button" onClick={run} disabled={busy}>
          <ShieldCheck size={14} /> {busy ? t('outreach.check.running') : t('outreach.check.run')}
        </button>
      </div>

      {error && <div className="unibox-banner">{String(error)}</div>}

      {report && (
        <>
          <div className={`spam-score spam-score-${bandFor(report.result.score)}`}>
            <span className="spam-score-number">{report.result.score.toFixed(1)}</span>
            <span>{t('outreach.check.scoreOf', { threshold: report.result.threshold })}</span>
            <span className="unibox-badge">{report.result.action}</span>
          </div>
          <p className="unibox-reply-hint">{t('outreach.check.lowerIsBetter')}</p>
          <p className="unibox-reply-hint">
            {t('outreach.check.sentAs', {
              host: report.result.sent_as.host,
              ip: report.result.sent_as.ip,
            })}
          </p>

          <div className="campaign-field">
            <label>{t('outreach.check.previewFor', { email: report.preview.lead_email })}</label>
            <div className="unibox-detail-text">
              <strong>{report.preview.subject}</strong>
              <div>{report.preview.body}</div>
            </div>
          </div>

          <h4 className="lead-detail-heading">{t('outreach.check.rules')}</h4>
          {report.result.rules.length === 0 && <div className="unibox-empty">{t('outreach.check.noRules')}</div>}
          {report.result.rules.map((rule) => (
            <div key={rule.name} className="spam-rule">
              <span className={rule.score > 0 ? 'spam-rule-bad' : 'spam-rule-good'}>
                {rule.score > 0 ? '+' : ''}
                {rule.score.toFixed(1)}
              </span>
              <span>{rule.name}</span>
              <span className="lead-history-source">{rule.description}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
